import Joi from "joi";
import firebase from "@/javascript/firebase";
import { setDoc, getPublicationDateTS, upsertTags } from "@/javascript/db";

const db = firebase.firestore();

const PAPER_SCHEMA = Joi.object({
  publicationDate: Joi.string().required(),
  title: Joi.string().required(),
  displayTitle: Joi.string().required(),
  summary: Joi.string().required(),
  description: Joi.string().required(),
  imgDescription: Joi.string().allow(""),
  expandedDescription: Joi.string().required(),
  didYouKnow: Joi.string().required(),
  didYouKnowSource: Joi.string()
    .uri({ scheme: ["http", "https"] })
    .required(),
  authors: Joi.array()
    .min(1)
    .items(Joi.string())
    .required(),
  journal: Joi.string().required(),
  sources: Joi.array()
    .items(
      Joi.object({
        displayName: Joi.string()
          .required()
          .allow(""),
        link: Joi.string()
          .uri({ scheme: ["http", "https"] })
          .required(),
      })
    )
    .required(),
  columnDisplayName: Joi.string().required(),
  relevantSNPs: Joi.array()
    .items(
      Joi.object({
        snp: Joi.string().required(),
        allele: Joi.string().required(),
        frequency: Joi.number()
          .min(0)
          .max(1)
          .required(),
        comment: Joi.string().allow(""),
        // Allow for p-values in exponential form
        statisticalSignificance: Joi.object({
          base: Joi.number(),
          exponent: Joi.number(),
        }).required(),
        gene: Joi.string(),
        newlyStudied: Joi.boolean(),
        effectSize: Joi.number().required(),
      })
    )
    .required(),
  links: Joi.array()
    .items(
      Joi.object({
        displayName: Joi.string().required(),
        link: Joi.string()
          .uri({ scheme: ["http", "https"] })
          .required(),
      })
    )
    .required(),
  img: Joi.string()
    .required()
    .allow(""),
  tags: Joi.object()
    .pattern(Joi.string().required(), Joi.boolean().required())
    .required(),
  additionalSNPs: Joi.array().items(Joi.string().allow("")).required(),
  dictionary: Joi.object().pattern(Joi.string(), Joi.string()),
  trait: Joi.string().required(),
  requiresSubscription: Joi.boolean().required(),
}).required();

/**
 * Admin function for adding papers.
 * The collection is keyed by the display name so it will overwrite.
 */
const postGenomicPaper = async (paper) => {
  const { error, value: genomicPaper } = PAPER_SCHEMA.validate(paper);
  if (error) return error;

  let { title, tags, publicationDate } = paper;
  const tagsCollection = Object.keys(tags);

  genomicPaper.tagsCollection = tagsCollection;
  genomicPaper.publicationDateTS = getPublicationDateTS(publicationDate);
  genomicPaper.dbSNPUpdateDone = false;
  genomicPaper.products = ['nebula-expand', 'nebula-1x', 'nebula-30x', 'nebula-100x'];

  // Document IDs can't contain `/`
  title = title.replace(/\//g, "");

  await Promise.all([
    setDoc("genomic-newsfeed", title, genomicPaper),
    upsertTags(tagsCollection),
  ]);

  const countSnap = await db.collection("genomic-newsfeed").get();
  const count = countSnap.size;

  await setDoc("counts", "genomic-newsfeed", { count });

  const snpCountSnap = await db
    .collection("counts")
    .doc("snps")
    .get();

  await snpCountSnap.ref.update({
    count: firebase.firestore.FieldValue.increment(10),
  });
};

export { postGenomicPaper };
